import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

const ConsentContext = createContext();

export const CONSENT_TYPES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  PREFERENCES: 'preferences'
};

export const CONSENT_COOKIE_NAME = 'lumat_consent_preferences';
export const CONSENT_COOKIE_EXPIRY = 365; // days

const defaultConsent = {
  [CONSENT_TYPES.NECESSARY]: true, // Always true, cannot be disabled
  [CONSENT_TYPES.ANALYTICS]: false,
  [CONSENT_TYPES.MARKETING]: false,
  [CONSENT_TYPES.PREFERENCES]: false
};

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(defaultConsent);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Load consent from cookies on mount
  useEffect(() => {
    const savedConsent = Cookies.get(CONSENT_COOKIE_NAME);
    
    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        // Ensure we merge with defaultConsent to handle any missing properties
        const mergedConsent = { ...defaultConsent, ...parsedConsent };
        setConsent(mergedConsent);
        setHasUserInteracted(true);
        setShowBanner(false);
      } catch (error) {
        console.error('Error parsing consent cookie:', error);
        // If cookie is corrupted, remove it and show banner
        Cookies.remove(CONSENT_COOKIE_NAME);
        setConsent(defaultConsent);
        setShowBanner(true);
      }
    } else {
      // No consent cookie found, show banner
      setConsent(defaultConsent);
      setShowBanner(true);
    }
  }, []);

  const updateConsent = (newConsent) => {
    const updatedConsent = {
      ...consent,
      ...newConsent,
      [CONSENT_TYPES.NECESSARY]: true // Always keep necessary cookies enabled
    };

    setConsent(updatedConsent);
    setHasUserInteracted(true);
    setShowBanner(false);

    // Save to cookie
    try {
      Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(updatedConsent), {
        expires: CONSENT_COOKIE_EXPIRY,
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax'
      });
      console.log('Consent saved to cookie:', updatedConsent);
    } catch (error) {
      console.error('Error saving consent cookie:', error);
    }

    // Trigger consent update event for analytics
    window.dispatchEvent(new CustomEvent('consentUpdated', {
      detail: updatedConsent
    }));
  };

  const acceptAll = () => {
    const allAccepted = Object.keys(CONSENT_TYPES).reduce((acc, key) => {
      acc[CONSENT_TYPES[key]] = true;
      return acc;
    }, {});
    updateConsent(allAccepted);
  };

  const rejectAll = () => {
    const allRejected = {
      [CONSENT_TYPES.NECESSARY]: true,
      [CONSENT_TYPES.ANALYTICS]: false,
      [CONSENT_TYPES.MARKETING]: false,
      [CONSENT_TYPES.PREFERENCES]: false
    };
    updateConsent(allRejected);
  };

  const resetConsent = () => {
    Cookies.remove(CONSENT_COOKIE_NAME);
    setConsent(defaultConsent);
    setHasUserInteracted(false);
    setShowBanner(true);
  };

  const hasConsent = (type) => {
    return consent[type] === true;
  };

  const value = {
    consent,
    hasUserInteracted,
    showBanner,
    updateConsent,
    acceptAll,
    rejectAll,
    resetConsent,
    hasConsent,
    setShowBanner
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}
